/**
 * Phase 32 — "How do I get this?" under a channel's target field: one quiet
 * link that opens the numbered walkthrough (with the pages to click),
 * closed by default so the card stays calm.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { ChevronRight, ExternalLink } from 'lucide-react';

import { cn } from '@/lib/utils';

import { HOW_TO, type HowToStep } from './how-to-steps';

const Step: FC<{ step: HowToStep }> = ({ step }) => (
  <li className="leading-relaxed">
    {step.text}
    {step.link ? (
      <>
        {' '}
        <a href={step.link.href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 font-medium text-primary underline-offset-2 hover:underline">
          {step.link.label}
          <ExternalLink className="h-3 w-3" aria-hidden />
        </a>
      </>
    ) : null}
  </li>
);

export const HowToGet: FC<{ channel: 'TELEGRAM' | 'SLACK' }> = ({ channel }) => {
  const [open, setOpen] = useState(false);
  const h = HOW_TO[channel];
  return (
    <div className="flex flex-col gap-1.5" data-testid={`howto-${channel.toLowerCase()}`}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="flex w-fit items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80">
        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform duration-200', open && 'rotate-90')} aria-hidden />
        {h.question}
      </button>
      {open ? (
        <div className="flex flex-col gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2.5 text-xs duration-300 animate-in fade-in-0 slide-in-from-top-1 motion-reduce:animate-none">
          <ol className="flex list-decimal flex-col gap-1.5 pl-4">
            {h.steps.map((s) => (
              <Step key={s.text} step={s} />
            ))}
          </ol>
          {h.alternative ? (
            <ol className="flex list-disc flex-col gap-1 border-t border-border/50 pl-4 pt-2 text-muted-foreground">
              {h.alternative.map((s) => (
                <Step key={s.text} step={s} />
              ))}
            </ol>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
